import React, { useState, useEffect, useCallback } from 'react';
import { transportAPI } from '../../services/api';
import { confirmDialog } from '../../utils/notify';

const TransportFeeConfigTab = () => {
  const [activeFeeMethod, setActiveFeeMethod] = useState('GLOBAL_COURSE');
  const [availableCourses, setAvailableCourses] = useState([]);
  const [globalFees, setGlobalFees] = useState({});
  const [distanceSlabs, setDistanceSlabs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Row-level lock/edit state for configured global fees
  const [editingCourses, setEditingCourses] = useState({});

  // Method Switch Confirmation Modal State
  const [showMethodConfirmModal, setShowMethodConfirmModal] = useState(false);
  const [targetMethod, setTargetMethod] = useState(null);

  // Slab Modal State
  const [showSlabModal, setShowSlabModal] = useState(false);
  const [editingSlabId, setEditingSlabId] = useState(null);
  const [slabFormData, setSlabFormData] = useState({
    min_distance: '',
    max_distance: '',
    course_fees: [
      { course: '', amount: '' }
    ]
  });

  const loadFeeConfigData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const [coursesRes, configRes] = await Promise.all([
        transportAPI.getAvailableCourses(),
        transportAPI.getFeeConfig()
      ]);

      const coursesArr = Array.isArray(coursesRes) ? coursesRes : [];
      setAvailableCourses(coursesArr);

      if (configRes) {
        setActiveFeeMethod(configRes.active_fee_method || 'GLOBAL_COURSE');

        const gfMap = {};
        (configRes.global_course_fees || []).forEach(gf => {
          gfMap[gf.course] = gf.amount;
        });
        setGlobalFees(gfMap);

        setDistanceSlabs(configRes.distance_slabs || []);
      }
    } catch (err) {
      console.error('Error loading fee configuration data:', err);
      setError(err.response?.data?.error || err.message || 'Failed to load transport fee configuration.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFeeConfigData();
  }, [loadFeeConfigData]);

  const handleInitiateMethodChange = (newMethod) => {
    if (newMethod === activeFeeMethod) return;
    setTargetMethod(newMethod);
    setShowMethodConfirmModal(true);
  };

  const handleConfirmMethodChange = async () => {
    if (!targetMethod) return;
    try {
      setSubmitting(true);
      setError(null);
      await transportAPI.selectFeeMethod(targetMethod);
      setActiveFeeMethod(targetMethod);
      setSuccessMsg(`Active transport fee method changed to ${targetMethod === 'GLOBAL_COURSE' ? 'Global Course-Based Fee' : 'Distance + Course-Based Slab Fee'}. All existing configurations were preserved.`);
      setShowMethodConfirmModal(false);
      setTimeout(() => setSuccessMsg(null), 5000);
    } catch (err) {
      console.error('Error selecting fee method:', err);
      setError(err.response?.data?.error || err.message || 'Failed to change fee method.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleToggleEditCourse = (courseCode) => {
    setEditingCourses(prev => ({
      ...prev,
      [courseCode]: !prev[courseCode]
    }));
  };

  const handleGlobalFeeChange = (courseCode, val) => {
    setGlobalFees(prev => ({
      ...prev,
      [courseCode]: val
    }));
  };

  const handleSaveGlobalFees = async (e) => {
    e.preventDefault();
    try {
      setSubmitting(true);
      setError(null);

      const feesPayload = availableCourses.map(c => ({
        course: c.code,
        amount: parseFloat(globalFees[c.code]) || 0
      }));

      await transportAPI.saveGlobalCourseFees(feesPayload);
      setSuccessMsg('Global course-based transport fees saved successfully.');
      setEditingCourses({});
      loadFeeConfigData();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error saving global course fees:', err);
      setError(err.response?.data?.error || err.message || 'Failed to save global course fees.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCreateSlabModal = () => {
    setEditingSlabId(null);

    const defaultCourseFees = availableCourses.map(c => ({
      course: c.code,
      amount: ''
    }));

    setSlabFormData({
      min_distance: '',
      max_distance: '',
      course_fees: defaultCourseFees.length > 0 ? defaultCourseFees : [{ course: '', amount: '' }]
    });
    setShowSlabModal(true);
  };

  const handleOpenEditSlabModal = (slab) => {
    setEditingSlabId(slab.id);

    const existingFeesMap = {};
    (slab.course_fees || []).forEach(cf => {
      existingFeesMap[cf.course] = cf.amount;
    });

    const mergedCourseFees = availableCourses.map(c => ({
      course: c.code,
      amount: existingFeesMap[c.code] !== undefined ? existingFeesMap[c.code] : ''
    }));

    setSlabFormData({
      min_distance: slab.min_distance || '',
      max_distance: slab.max_distance || '',
      course_fees: mergedCourseFees.length > 0 ? mergedCourseFees : [{ course: '', amount: '' }]
    });
    setShowSlabModal(true);
  };

  const handleAddSlabCourseFee = () => {
    setSlabFormData(prev => ({
      ...prev,
      course_fees: [...prev.course_fees, { course: '', amount: '' }]
    }));
  };

  const handleRemoveSlabCourseFee = (index) => {
    setSlabFormData(prev => ({
      ...prev,
      course_fees: prev.course_fees.filter((_, idx) => idx !== index)
    }));
  };

  const handleSlabCourseFeeChange = (index, field, value) => {
    setSlabFormData(prev => {
      const updated = [...prev.course_fees];
      updated[index] = { ...updated[index], [field]: value };
      return { ...prev, course_fees: updated };
    });
  };

  const handleSaveSlab = async (e) => {
    e.preventDefault();
    const minD = parseFloat(slabFormData.min_distance);
    const maxD = parseFloat(slabFormData.max_distance);

    if (isNaN(minD) || isNaN(maxD)) {
      setError('Minimum and Maximum distance must be valid numbers.');
      return;
    }
    if (minD >= maxD) {
      setError('Maximum distance must be strictly greater than minimum distance.');
      return;
    }

    const validCourseFees = slabFormData.course_fees
      .filter(cf => cf.course.trim() !== '')
      .map(cf => ({
        course: cf.course.trim(),
        amount: parseFloat(cf.amount) || 0
      }));

    const courseCodes = validCourseFees.map(cf => cf.course);
    const duplicates = courseCodes.filter((item, index) => courseCodes.indexOf(item) !== index);
    if (duplicates.length > 0) {
      setError(`Duplicate course selection: ${duplicates.join(', ')}. Each course can be configured at most once per slab.`);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);

      const payload = {
        min_distance: minD,
        max_distance: maxD,
        course_fees: validCourseFees
      };

      if (editingSlabId) {
        await transportAPI.updateDistanceSlab(editingSlabId, payload);
        setSuccessMsg(`Distance pricing slab (${minD}–${maxD} KM) updated successfully.`);
      } else {
        await transportAPI.createDistanceSlab(payload);
        setSuccessMsg(`Distance pricing slab (${minD}–${maxD} KM) created successfully.`);
      }

      setShowSlabModal(false);
      loadFeeConfigData();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error saving distance slab:', err);
      setError(err.response?.data?.error || err.response?.data?.min_distance || err.response?.data?.max_distance || err.message || 'Failed to save distance slab.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFinalizeSlab = async (slabId, slabName) => {
    try {
      setError(null);
      await transportAPI.finalizeDistanceSlab(slabId);
      setSuccessMsg(`Distance pricing slab "${slabName}" finalized successfully.`);
      loadFeeConfigData();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error finalizing slab:', err);
      setError(err.response?.data?.error || err.message || 'Failed to finalize slab.');
    }
  };

  const handleDeleteSlab = async (slabId, slabName) => {
    if (!await confirmDialog(`Are you sure you want to delete distance slab "${slabName}"?`)) return;
    try {
      setError(null);
      await transportAPI.deleteDistanceSlab(slabId);
      setSuccessMsg(`Distance pricing slab "${slabName}" deleted.`);
      loadFeeConfigData();
      setTimeout(() => setSuccessMsg(null), 4000);
    } catch (err) {
      console.error('Error deleting slab:', err);
      setError(err.response?.data?.error || err.message || 'Failed to delete slab.');
    }
  };

  if (loading) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading transport fee configuration...</div>;
  }

  return (
    <div className="tm-card" style={{ marginTop: 20 }}>
      {/* Header Bar */}
      <div style={{ marginBottom: 24 }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
          <span>💰</span> Institution Transport Fee Configuration
        </h3>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: '#64748b' }}>
          Configure institution-wide transport fee calculation rules. Reference existing course records without assigning student fees.
        </p>
      </div>

      {/* Notifications */}
      {error && (
        <div className="alert alert-danger" style={{ marginBottom: 16, borderRadius: 10, padding: '12px 16px', background: '#fff1f2', border: '1px solid #fecdd3', color: '#9f1239', fontSize: 13 }}>
          <span>⚠️</span> {error}
        </div>
      )}
      {successMsg && (
        <div className="alert alert-success" style={{ marginBottom: 16, borderRadius: 10, padding: '12px 16px', background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#047857', fontSize: 13 }}>
          <span>✓</span> {successMsg}
        </div>
      )}

      {/* Fee Method Selection Card */}
      <div style={{ background: '#f8fafc', borderRadius: 16, border: '1px solid #cbd5e1', padding: 20, marginBottom: 28 }}>
        <h4 style={{ margin: '0 0 12px', fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
          Active Transport Fee Calculation Method
        </h4>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
          <div
            onClick={() => handleInitiateMethodChange('GLOBAL_COURSE')}
            style={{
              background: activeFeeMethod === 'GLOBAL_COURSE' ? '#eff6ff' : '#ffffff',
              border: `2px solid ${activeFeeMethod === 'GLOBAL_COURSE' ? '#2563eb' : '#e2e8f0'}`,
              borderRadius: 12,
              padding: 16,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <input
                type="radio"
                name="feeMethod"
                checked={activeFeeMethod === 'GLOBAL_COURSE'}
                onChange={() => handleInitiateMethodChange('GLOBAL_COURSE')}
              />
              <strong style={{ fontSize: 14, color: activeFeeMethod === 'GLOBAL_COURSE' ? '#1e40af' : '#0f172a' }}>
                Method 1 — Global Course-Based Fee
              </strong>
            </div>
            <p style={{ margin: '0 0 0 28px', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
              A single fixed transport fee configured for each course, independent of route distance.
            </p>
          </div>

          <div
            onClick={() => handleInitiateMethodChange('DISTANCE_COURSE_SLAB')}
            style={{
              background: activeFeeMethod === 'DISTANCE_COURSE_SLAB' ? '#eff6ff' : '#ffffff',
              border: `2px solid ${activeFeeMethod === 'DISTANCE_COURSE_SLAB' ? '#2563eb' : '#e2e8f0'}`,
              borderRadius: 12,
              padding: 16,
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
              <input
                type="radio"
                name="feeMethod"
                checked={activeFeeMethod === 'DISTANCE_COURSE_SLAB'}
                onChange={() => handleInitiateMethodChange('DISTANCE_COURSE_SLAB')}
              />
              <strong style={{ fontSize: 14, color: activeFeeMethod === 'DISTANCE_COURSE_SLAB' ? '#1e40af' : '#0f172a' }}>
                Method 2 — Distance + Course-Based Slab Fee
              </strong>
            </div>
            <p style={{ margin: '0 0 0 28px', fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>
              Configurable distance slabs (e.g. 0–10 KM, 10–20 KM) with specific course-wise fees per slab.
            </p>
          </div>
        </div>
      </div>

      {/* METHOD 1 VIEW: GLOBAL COURSE-BASED FEES */}
      {activeFeeMethod === 'GLOBAL_COURSE' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                Global Course-Based Transport Fees
              </h4>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>
                Define transport fees for all active courses fetched dynamically from existing Course records.
              </p>
            </div>
          </div>

          <form onSubmit={handleSaveGlobalFees}>
            <div className="table-container" style={{ marginBottom: 20 }}>
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Course Name</th>
                    <th>Configured Global Transport Fee (₹)</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {availableCourses.map(course => {
                    const feeVal = globalFees[course.code] !== undefined ? globalFees[course.code] : '';
                    const isConfigured = feeVal !== '' && parseFloat(feeVal) > 0;
                    const isEditing = !!editingCourses[course.code];
                    const isDisabled = isConfigured && !isEditing;

                    return (
                      <tr key={course.code}>
                        <td>
                          <strong>{course.name}</strong>
                        </td>
                        <td style={{ maxWidth: 240 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontWeight: 600, color: '#475569' }}>₹</span>
                            <input
                              type="number"
                              min="0"
                              step="100"
                              disabled={isDisabled}
                              placeholder="e.g. 20000"
                              value={feeVal}
                              onChange={e => handleGlobalFeeChange(course.code, e.target.value)}
                              className="driver-input"
                              style={{
                                margin: 0,
                                background: isDisabled ? '#f1f5f9' : '#ffffff',
                                color: isDisabled ? '#475569' : '#0f172a',
                                cursor: isDisabled ? 'not-allowed' : 'text',
                                fontWeight: isDisabled ? 600 : 400
                              }}
                            />
                          </div>
                        </td>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            {isConfigured ? (
                              <>
                                <span className={`badge-status ${isEditing ? 'pending' : 'verified'}`} style={{ fontSize: 11, background: isEditing ? '#eff6ff' : undefined, color: isEditing ? '#1d4ed8' : undefined, borderColor: isEditing ? '#bfdbfe' : undefined }}>
                                  {isEditing ? 'Editing...' : `Configured (₹${parseFloat(feeVal).toLocaleString('en-IN')})`}
                                </span>
                                {!isEditing && (
                                  <button
                                    type="button"
                                    onClick={() => handleToggleEditCourse(course.code)}
                                    style={{
                                      padding: '4px 10px',
                                      borderRadius: 6,
                                      border: '1px solid #cbd5e1',
                                      background: '#ffffff',
                                      color: '#334155',
                                      fontSize: 12,
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 4
                                    }}
                                    title="Enable editing for this course fee"
                                  >
                                    ✏️ Edit
                                  </button>
                                )}
                              </>
                            ) : (
                              <span className="badge-status pending" style={{ fontSize: 11, background: '#fff7ed', color: '#c2410c', borderColor: '#ffedd5' }}>
                                Transport Fee Not Configured
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="submit"
                disabled={submitting}
                className="driver-btn-primary"
                style={{ width: 'auto', padding: '10px 24px' }}
              >
                {submitting ? 'Saving Configuration...' : 'Save Global Course Fees'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* METHOD 2 VIEW: DISTANCE + COURSE-BASED SLAB FEES */}
      {activeFeeMethod === 'DISTANCE_COURSE_SLAB' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
            <div>
              <h4 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#0f172a' }}>
                Distance + Course-Based Slab Fees
              </h4>
              <p style={{ margin: '2px 0 0', fontSize: 13, color: '#64748b' }}>
                Manage distance slabs with course-wise fees. Range boundary: min_distance ≤ distance &lt; max_distance.
              </p>
            </div>
            <button
              onClick={handleOpenCreateSlabModal}
              className="driver-btn-primary"
              style={{ width: 'auto', padding: '10px 20px', borderRadius: 10, display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <span>➕</span> Create Distance Slab
            </button>
          </div>

          {distanceSlabs.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8', background: '#f8fafc', borderRadius: 12, border: '1px dashed #cbd5e1' }}>
              No distance slabs configured. Click <strong>"+ Create Distance Slab"</strong> to add your first distance slab (e.g. 0–10 KM).
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: 20 }}>
              {distanceSlabs.map(slab => (
                <div
                  key={slab.id}
                  style={{
                    background: '#ffffff',
                    border: '1px solid #e2e8f0',
                    borderRadius: 16,
                    padding: 20,
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.03)'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, borderBottom: '1px solid #f1f5f9', paddingBottom: 10 }}>
                    <div>
                      <h5 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: '#0f172a' }}>
                        📏 {slab.slab_name || `${slab.min_distance}–${slab.max_distance} KM`}
                      </h5>
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>
                        {slab.min_distance} ≤ Distance &lt; {slab.max_distance} KM
                      </div>
                    </div>
                    <span className={`badge-status ${slab.is_finalized ? 'verified' : 'pending'}`}>
                      {slab.is_finalized ? 'Finalized' : 'Draft'}
                    </span>
                  </div>

                  {/* Course Fees Table in Slab */}
                  <div style={{ marginBottom: 16 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', textAlign: 'left', color: '#64748b', fontSize: 12 }}>
                          <th style={{ padding: '6px 10px' }}>Course</th>
                          <th style={{ padding: '6px 10px', textAlign: 'right' }}>Amount (₹)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(slab.course_fees || []).length === 0 ? (
                          <tr>
                            <td colSpan="2" style={{ padding: 12, textAlign: 'center', color: '#94a3b8' }}>
                              No course fees configured in this slab.
                            </td>
                          </tr>
                        ) : (
                          (slab.course_fees || []).map(cf => (
                            <tr key={cf.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '8px 10px' }}>
                                <strong>{cf.course_display || cf.course}</strong>
                              </td>
                              <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700, color: '#0f172a' }}>
                                ₹{parseFloat(cf.amount).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 8, borderTop: '1px solid #f1f5f9' }}>
                    {!slab.is_finalized && (
                      <button
                        onClick={() => handleFinalizeSlab(slab.id, slab.slab_name)}
                        className="btn-sm btn-success"
                        style={{ padding: '5px 12px', fontSize: 12 }}
                      >
                        ✓ Finalize Slab
                      </button>
                    )}
                    <button
                      onClick={() => handleOpenEditSlabModal(slab)}
                      style={{ padding: '5px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                    >
                      ✏️ Edit
                    </button>
                    <button
                      onClick={() => handleDeleteSlab(slab.id, slab.slab_name)}
                      className="btn-sm btn-danger"
                      style={{ padding: '5px 12px', fontSize: 12 }}
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* METHOD SWITCH CONFIRMATION MODAL */}
      {showMethodConfirmModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
              Confirm Active Fee Method Switch
            </h3>
            <p style={{ fontSize: 13, color: '#475569', lineHeight: 1.5, marginBottom: 20 }}>
              Changing the active fee method will change which transport pricing configuration is used for calculation. <strong>Existing configurations for both methods will be preserved.</strong>
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12 }}>
              <button
                type="button"
                onClick={() => setShowMethodConfirmModal(false)}
                style={{ padding: '8px 16px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleConfirmMethodChange}
                className="driver-btn-primary"
                style={{ width: 'auto', padding: '8px 20px' }}
              >
                {submitting ? 'Updating...' : 'Confirm & Switch Method'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* CREATE / EDIT SLAB MODAL */}
      {showSlabModal && (
        <div className="modal-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.65)', backdropFilter: 'blur(4px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div className="modal-content" style={{ background: '#fff', borderRadius: 16, width: '100%', maxWidth: 620, maxHeight: '90vh', overflowY: 'auto', padding: 24, boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, borderBottom: '1px solid #e2e8f0', paddingBottom: 12 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: '#0f172a' }}>
                {editingSlabId ? 'Edit Distance Pricing Slab' : 'Create New Distance Pricing Slab'}
              </h3>
              <button onClick={() => setShowSlabModal(false)} style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: '#64748b' }}>
                &times;
              </button>
            </div>

            <form onSubmit={handleSaveSlab}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="driver-form-group">
                <div>
                  <label className="driver-label">Minimum Distance (KM) *</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    required
                    placeholder="e.g. 0"
                    value={slabFormData.min_distance}
                    onChange={e => setSlabFormData({ ...slabFormData, min_distance: e.target.value })}
                    className="driver-input"
                  />
                </div>
                <div>
                  <label className="driver-label">Maximum Distance (KM) *</label>
                  <input
                    type="number"
                    step="0.1"
                    min="0"
                    required
                    placeholder="e.g. 10"
                    value={slabFormData.max_distance}
                    onChange={e => setSlabFormData({ ...slabFormData, max_distance: e.target.value })}
                    className="driver-input"
                  />
                </div>
              </div>

              {/* Course-Wise Fees inside Slab */}
              <div style={{ marginTop: 20, padding: 16, background: '#f8fafc', borderRadius: 12, border: '1px solid #e2e8f0' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <h4 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
                    Course Transport Fees for this Slab
                  </h4>
                  <button
                    type="button"
                    onClick={handleAddSlabCourseFee}
                    style={{ padding: '4px 10px', background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1d4ed8', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer' }}
                  >
                    ➕ Add Course Row
                  </button>
                </div>

                {slabFormData.course_fees.map((cf, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '2fr 1.5fr auto', gap: 10, alignItems: 'center', marginBottom: 10 }}>
                    <div>
                      <select
                        required
                        value={cf.course}
                        onChange={e => handleSlabCourseFeeChange(idx, 'course', e.target.value)}
                        className="driver-input"
                        style={{ margin: 0 }}
                      >
                        <option value="">Select Existing Course ▼</option>
                        {availableCourses.map(c => (
                          <option key={c.code} value={c.code}>
                            {c.name} ({c.code})
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <input
                        type="number"
                        min="0"
                        step="100"
                        required
                        placeholder="Transport Fee Amount (₹)"
                        value={cf.amount}
                        onChange={e => handleSlabCourseFeeChange(idx, 'amount', e.target.value)}
                        className="driver-input"
                        style={{ margin: 0 }}
                      />
                    </div>
                    <div>
                      {slabFormData.course_fees.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSlabCourseFee(idx)}
                          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fecdd3', background: '#fff1f2', color: '#e11d48', cursor: 'pointer' }}
                        >
                          🗑️
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 24, borderTop: '1px solid #e2e8f0', paddingTop: 16 }}>
                <button
                  type="button"
                  onClick={() => setShowSlabModal(false)}
                  style={{ padding: '10px 20px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="driver-btn-primary"
                  style={{ width: 'auto', padding: '10px 24px' }}
                >
                  {submitting ? 'Saving Slab...' : editingSlabId ? 'Update Slab' : 'Save & Finalize Slab'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TransportFeeConfigTab;
